import React from 'react'
import PropTypes from 'prop-types'
import PhotoEditStatusBadge from 'domains/seller-inventory-batch/atoms/photo-edit-status-badge'
import PhotoReceivedStatusBadge from 'domains/seller-inventory-batch/atoms/photo-received-status-badge'
import PhotographyStatusBadge from 'domains/seller-inventory-batch/atoms/photography-status-badge'
import TimeUtil from 'utils/time-util'
import { RowButton } from 'components/atoms'

const propTypes = {
  buttonText       : PropTypes.string,
  buttonColour     : PropTypes.string,
  isButtonDisabled : PropTypes.func,
  onButtonClick    : PropTypes.func
}

/**
 * Seller Inventory Batch Curation Accepted Item Table Columns
 * used in seller inventory batch items list to show the whole items
 *
 * It's important that we're using React.useMemo
 * here to ensure that our data isn't recreated on every render.
 * see https://react-table.tanstack.com/docs/quick-start
 *
 * Columns definition for Seller Product Table
 * @param {string} buttonText - The text that appears at the button on each row
 * @param {string} buttonColour - Bootstrap button type class name (eg. primary)
 * @param {Function} isButtonDisabled - function that will check whether the button on particular row is disabled
 * @param {Function} onButtonClick - function that will run when the button is clicked
 */
const processedItemTableColumns = ({
  buttonText,
  buttonColour,
  isButtonDisabled,
  onButtonClick
}) => {

  const ProductNameCell = (row) => <div className='text-wrap'>{ row.cell.value }</div>

  const DateCell = (row) => TimeUtil.format(row.cell.value)

  const PhotoReceivedStatusCell = (row) => <PhotoReceivedStatusBadge photo_received_status={ row.cell.value } />

  const PhotographyStatusCell = (row) => <PhotographyStatusBadge photography_status={ row.cell.value } />

  const PhotoEditStatusCell = (row) => <PhotoEditStatusBadge photo_edit_status={ row.cell.value } />

  // eslint-disable-next-line react/prop-types
  const ActionCell = ({ cell: { row } }) => {

    // eslint-disable-next-line react/prop-types
    const disabled = isButtonDisabled ? isButtonDisabled(row.original) : false

    return (
      <RowButton
        // eslint-disable-next-line react/prop-types
        data={ row.original }
        color={ buttonColour }
        disabled={ disabled }
        onClick={ onButtonClick }
        text={ buttonText }
      />
    )
  }

  return (
    [
      {
        Header         : 'Inv Batch Code',
        accessor       : 'inventory_batch_code',
        id             : 'inventory_batch_code',
        disableFilters : true
      },
      {
        Header   : 'SKU',
        accessor : 'sku',
        id       : 'sku'
      },
      {
        Header         : 'Product name',
        accessor       : 'name',
        id             : 'name',
        Cell           : ProductNameCell,
        disableFilters : true
      },
      {
        Header         : 'Defect Note',
        accessor       : 'defect_note',
        id             : 'defect_note',
        disableFilters : true
      },
      {
        Header         : <>Photo Studio <br />Received Status</>,
        accessor       : 'photo_received_status',
        id             : 'photo_received_status',
        Cell           : PhotoReceivedStatusCell,
        disableFilters : true
      },
      {
        Header         : <>Photo <br />Received Date</>,
        accessor       : 'photo_received_date',
        id             : 'photo_received_date',
        Cell           : DateCell,
        disableFilters : true
      },
      {
        Header         : 'Photography Status',
        accessor       : 'photography_status',
        id             : 'photography_status',
        Cell           : PhotographyStatusCell,
        disableFilters : true
      },
      {
        Header         : 'Photographed Date',
        accessor       : 'photography_date',
        id             : 'photography_date',
        Cell           : DateCell,
        disableFilters : true
      },
      {
        Header         : 'Photographed By',
        accessor       : 'photography_by.name',
        id             : 'photography_by',
        disableFilters : true
      },
      {
        Header         : 'Photo Edit Status',
        accessor       : 'photo_edit_status',
        id             : 'photo_edit_status',
        Cell           : PhotoEditStatusCell,
        disableFilters : true
      },
      {
        Header         : 'Photo Edited Date',
        accessor       : 'photo_edited_date',
        id             : 'photo_edited_date',
        Cell           : DateCell,
        disableFilters : true
      },
      {
        Header         : 'Photo Edited By',
        accessor       : 'photo_edited_by.name',
        id             : 'photo_edited_by',
        disableFilters : true
      },
      {
        Header         : 'Put Away Date',
        id             : 'put_away_date',
        accessor       : 'put_away_date',
        Cell           : DateCell,
        disableFilters : true
      },
      {
        Header         : 'Put Away By',
        id             : 'put_away_by',
        accessor       : 'put_away_by.name',
        disableFilters : true
      },
      {
        Header : 'Action',
        id     : 'action',
        Cell   : ActionCell
      }
    ]
  )
}

processedItemTableColumns.propTypes = propTypes

export default processedItemTableColumns